import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, MessageCircle, Lock } from "lucide-react";
import { useLang } from "../i18n";
import { RESTAURANT, HOURS, DAY_INDEX_TO_KEY } from "../data";
import { useSettings } from "../SiteSettings";
import OliveBranch from "./OliveBranch";

export const Footer = () => {
  const { t } = useLang();
  const settings = useSettings();
  const R = { ...RESTAURANT, ...(settings.contact || {}) };
  const whatsappDigits = (R.whatsapp || "").replace(/\D/g, "") || RESTAURANT.whatsappDigits;
  const hours = Array.isArray(settings.hours) && settings.hours.length ? settings.hours : HOURS;
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();
  const todayKey = DAY_INDEX_TO_KEY[new Date().getDay()];

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = ["about", "dishes", "gallery", "reviews", "contact"];

  return (
    <footer className="bg-black text-white pt-20 pb-8 px-5 md:px-10 border-t border-qs-border">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <img src="/logo-mark-white.png" alt="Pizzeria Quattro Stelle Logo" className="h-28 w-auto object-contain -ml-1" />
            <OliveBranch className="w-24 text-qs-gold my-5" />
            <p className="font-outfit text-white/70 text-sm leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
            <div className="flex gap-3 mt-5">
              <a href={RESTAURANT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="w-10 h-10 flex items-center justify-center border border-white/20 hover:bg-qs-gold hover:border-qs-gold transition-colors">
                <Instagram size={17} />
              </a>
              <a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="w-10 h-10 flex items-center justify-center border border-white/20 hover:bg-qs-gold hover:border-qs-gold transition-colors">
                <MessageCircle size={17} />
              </a>
              <a href={`tel:${R.phoneIntl}`} data-testid="footer-phone" className="w-10 h-10 flex items-center justify-center border border-white/20 hover:bg-qs-gold hover:border-qs-gold transition-colors">
                <Phone size={17} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-cormorant text-xl mb-4 text-qs-gold">{t.footer.quickLinks}</h4>
            <ul className="space-y-2.5 font-outfit text-sm">
              {links.map((l) => (
                <li key={l}>
                  <button onClick={() => goSection(l)} className="text-white/70 hover:text-white transition-colors">
                    {t.nav[l]}
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => navigate("/speisekarte")} className="text-white/70 hover:text-white transition-colors">
                  {t.nav.menu}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-cormorant text-xl mb-4 text-qs-gold">{t.footer.contact}</h4>
            <ul className="space-y-3 font-outfit text-sm text-white/70">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-qs-gold mt-0.5 shrink-0" />
                <span>{R.street}, {R.city}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-qs-gold shrink-0" />
                <a href={`tel:${R.phoneIntl}`} className="hover:text-white transition-colors">{R.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-qs-gold shrink-0" />
                <a href={`mailto:${R.email}`} className="hover:text-white transition-colors break-all">{R.email}</a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-cormorant text-xl mb-4 text-qs-gold">{t.footer.hours}</h4>
            <ul className="space-y-1.5 font-outfit text-sm">
              {hours.map((d) => (
                <li key={d.key} className={`flex justify-between gap-3 ${d.key === todayKey ? "text-qs-gold" : "text-white/70"}`}>
                  <span>{t.hours.days[d.key]}</span>
                  <span className="text-right tabular-nums">
                    {d.closed ? t.hours.closed : (d.slots || []).join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-outfit text-xs text-white/50">
            © {year} {R.name}. {t.footer.rights}
          </p>
          <div className="flex items-center gap-5 font-outfit text-xs">
            <button data-testid="footer-impressum" onClick={() => navigate("/impressum")} className="text-white/50 hover:text-qs-gold transition-colors">
              {t.footer.impressum}
            </button>
            <button data-testid="footer-datenschutz" onClick={() => navigate("/datenschutz")} className="text-white/50 hover:text-qs-gold transition-colors">
              {t.footer.datenschutz}
            </button>
            <button data-testid="footer-admin" onClick={() => navigate("/admin")} title="Admin-Login" className="flex items-center gap-1.5 text-white/40 hover:text-qs-gold transition-colors">
              <Lock size={12} /> Admin
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
